import {createEntityAdapter, EntityAdapter, EntityState} from "@ngrx/entity";
import {BillingActModel} from "@services/back-office/model/billingActModel";
import {PaginationModel} from "@shared/models/pagination.model";
import {BillingActSearchModel} from "@services/back-office/model/billingActSearchModel";
import {ConfigService} from "@config/config.service";
import {ActsActions, ActsActionTypes} from "@billing/ngrx/actions/acts.actions";

export const adapter: EntityAdapter<BillingActModel> = createEntityAdapter<BillingActModel>();

export interface State extends EntityState<BillingActModel> {
    loading: boolean,
    loadingItem: string
    pagination: PaginationModel,
    filter: BillingActSearchModel
}

export const initialState: State = adapter.getInitialState({
    loading: false,
    loadingItem: '',
    pagination: {
        first: true,
        last: false,
        number: 0,
        numberOfElements: 0,
        size: new ConfigService().paginationConfig.pageSize,
        totalElements: 0,
    },
    filter: {}
});

export function reducer(state = initialState, action: ActsActions): State {
    switch (action.type) {
        case ActsActionTypes.LoadActs:
            return {...state, loading: !action.payload?.dontShowLoading};

        case ActsActionTypes.LoadActsSuccess:
            return adapter.setAll(action.payload.content!, {
                ...state,
                loading: false,
                pagination: {
                    size: action.payload.size,
                    last: action.payload.last,
                    totalElements: action.payload.totalElements,
                    numberOfElements: action.payload.numberOfElements,
                    first: action.payload.first,
                    number: action.payload.number,
                }
            });

        case ActsActionTypes.LoadActsFailure:
            return {...state, loading: false};

        case ActsActionTypes.EditAct:
            return {...state};

        case ActsActionTypes.EditActSuccess:
            return adapter.updateOne({id: action.payload.id!, changes: action.payload}, {...state});

        case ActsActionTypes.SignAct:
            return {...state, loadingItem: action.payload};

        case ActsActionTypes.SignActSuccess:
            return adapter.updateOne({id: action.payload.id!, changes: action.payload}, {...state, loadingItem: ''});

        case ActsActionTypes.SignActFailure:
            return {...state, loadingItem: ''};

        case ActsActionTypes.ToggleActSignedState:
            return {...state, loadingItem: action.payload.act.id!};

        case ActsActionTypes.ToggleActSignedStateSuccess:
            // eslint-disable-next-line no-case-declarations
            const newSignType = action.payload.signed
                ? 'MANUALLY_SIGNED'
                : action.payload.act?.signersDah?.length && action.payload.act?.signersClient?.length
                    ? 'ONLINE_SIGNED'
                    : 'NOT_SIGNED';

            return adapter.updateOne({id: action.payload.act.id!, changes: {...action.payload.act, signType: newSignType}}, {...state, loadingItem: ''});

        case ActsActionTypes.ToggleActSignedStateFailure:
            return {...state, loadingItem: ''};

        case ActsActionTypes.ChangePage:
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    number: action.payload.number,
                    size: action.payload.size
                }
            };

        case ActsActionTypes.SetInitialPagination:
            return {...state, pagination: initialState.pagination};

        case ActsActionTypes.SetFilter:
            return {...state, filter: action.payload};

        case ActsActionTypes.ClearFilter:
            return {...initialState};

        default:
            return state;
    }
}

export const {
    selectIds: selectActsIds,
    selectAll: selectAllActs
} = adapter.getSelectors();
